import React from 'react';
import { Printer, Download, Edit2, CheckCircle, FileText, X, Building, Calendar, DollarSign } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import Modal from '../../shared/components/Modal';
import { formatDateFR } from '../../shared/utils/helpers';
import { printInvoice } from '../utils/printInvoice';

export default function InvoicePreviewModal({ invoice, settings = {}, onEdit, onRecordPayment, onClose }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  const cur = settings?.currency || 'MAD';

  const isManual = !!(invoice.isManual || invoice.items || invoice.invoiceNumber);
  const invoiceNum = invoice.invoiceNumber || (invoice.id ? String(invoice.id).substring(0, 8).toUpperCase() : 'FAC-0000');
  const invoiceDate = invoice.date || invoice.invoiceDate || (invoice.createdAt ? String(invoice.createdAt).split('T')[0] : '');
  const dueDate = invoice.dueDate || '';

  const clientName = invoice.companyName || invoice.clientName || '—';
  const clientAddress = invoice.companyAddress || invoice.clientAddress || '—';
  const clientPhone = invoice.companyPhone || invoice.clientPhone || '—';
  const clientEmail = invoice.companyEmail || invoice.clientEmail || '—';
  const clientICE = invoice.companyICE || invoice.ice || '';
  const clientIF = invoice.companyIF || invoice.if || '';
  const clientRC = invoice.companyRC || invoice.rc || '';
  const clientCIN = invoice.companyCin || invoice.clientCin || '';

  const subtotalHT = Number(invoice.subtotalHT) || Number(invoice.totalAmount) || 0;
  const discountAmount = Number(invoice.discountAmount) || 0;
  const tvaAmount = Number(invoice.tvaAmount) || 0;
  const totalTTC = Number(invoice.totalTTC || invoice.totalAmount) || 0;
  const paidAmount = Number(invoice.paidAmount) || 0;
  const remainingAmount = Math.max(0, totalTTC - paidAmount);

  const status = invoice.status || (remainingAmount === 0 ? 'paid' : 'issued');

  const statusBadge = () => {
    switch (status) {
      case 'paid':
        return <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-bold">{isAr ? 'مدفوعة' : 'Payée'}</span>;
      case 'partial':
        return <span className="px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-bold">{isAr ? 'مدفوعة جزئياً' : 'Partiellement payée'}</span>;
      case 'unpaid':
        return <span className="px-2.5 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold">{isAr ? 'غير مدفوعة' : 'Impayée'}</span>;
      case 'draft':
        return <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-bold">{isAr ? 'مسودة' : 'Brouillon'}</span>;
      default:
        return <span className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">{isAr ? 'صادرة' : 'Émise'}</span>;
    }
  };

  const handlePrint = () => {
    printInvoice(invoice, settings);
  };

  return (
    <Modal onClose={onClose} title={`${isAr ? 'معاينة الفاتورة' : 'Facture'} #${invoiceNum}`}>
      <div className="space-y-6">
        
        {/* Action Header Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 border border-slate-200 p-3.5 rounded-2xl">
          <div className="flex items-center gap-2">
            {statusBadge()}
            <span className="text-xs text-slate-500 font-medium">
              {formatDateFR(invoiceDate)}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {onEdit && (
              <button
                type="button"
                onClick={() => { onClose(); onEdit(invoice); }}
                className="px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-sm"
              >
                <Edit2 className="w-3.5 h-3.5" />
                {isAr ? 'تعديل' : 'Modifier'}
              </button>
            )}

            {remainingAmount > 0 && onRecordPayment && (
              <button
                type="button"
                onClick={() => { onClose(); onRecordPayment(invoice); }}
                className="px-3 py-1.5 bg-emerald-50 border border-emerald-300 hover:bg-emerald-100 text-emerald-700 rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-sm"
              >
                <DollarSign className="w-3.5 h-3.5" />
                {isAr ? 'تسجيل دفعة' : 'Payer'}
              </button>
            )}

            <button
              type="button"
              onClick={handlePrint}
              className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-sm shadow-blue-500/20"
            >
              <Printer className="w-3.5 h-3.5" />
              {isAr ? 'طباعة / PDF' : 'Imprimer / PDF'}
            </button>
          </div>
        </div>

        {/* Invoice Visual Sheet Preview */}
        <div className="border border-slate-200 rounded-2xl p-6 bg-white shadow-sm space-y-6">
          
          {/* Header Info */}
          <div className="flex flex-wrap justify-between items-start gap-4 border-b border-slate-200 pb-5">
            <div>
              <div className="text-xl font-black text-slate-900 uppercase tracking-wide">
                {settings.companyName || 'AGENCE'}
              </div>
              <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                {settings.companyAddress && <div>{settings.companyAddress}</div>}
                {settings.companyPhone && <div>Tel: {settings.companyPhone}</div>}
                {settings.companyEmail && <div>Email: {settings.companyEmail}</div>}
                {(settings.companyICE || settings.companyRC || settings.companyIF) && (
                  <div className="font-mono text-slate-600 mt-1">
                    {settings.companyICE && <span>ICE: {settings.companyICE} </span>}
                    {settings.companyRC && <span>RC: {settings.companyRC} </span>}
                    {settings.companyIF && <span>IF: {settings.companyIF}</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="text-right">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{isAr ? 'فاتورة' : 'FACTURE'}</div>
              <div className="text-xl font-bold font-mono text-blue-700 mt-0.5">#{invoiceNum}</div>
              <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                <div>{isAr ? 'التاريخ:' : 'Date:'} <span className="font-semibold text-slate-700">{formatDateFR(invoiceDate)}</span></div>
                {dueDate && <div>{isAr ? 'الاستحقاق:' : 'Échéance:'} <span className="font-semibold text-slate-700">{formatDateFR(dueDate)}</span></div>}
              </div>
            </div>
          </div>

          {/* Client & Billing Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                {isAr ? 'معلومات الزبون / الشركة' : 'Destinataire (Client / Société)'}
              </div>
              <div className="text-sm font-bold text-slate-900">{clientName}</div>
              <div className="text-xs text-slate-600 space-y-1 mt-1">
                {clientAddress !== '—' && <div>📍 {clientAddress}</div>}
                {clientPhone !== '—' && <div>📞 {clientPhone}</div>}
                {clientEmail !== '—' && <div>✉️ {clientEmail}</div>}
                {(clientICE || clientIF || clientRC) && (
                  <div className="font-mono text-[11px] text-blue-800 bg-blue-50/80 p-1.5 rounded mt-1.5 inline-block">
                    {clientICE && <span>ICE: {clientICE} </span>}
                    {clientIF && <span>IF: {clientIF} </span>}
                    {clientRC && <span>RC: {clientRC} </span>}
                  </div>
                )}
                {clientCIN && <div>CIN/Pass: {clientCIN}</div>}
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                {isAr ? 'تفاصيل الأداء' : 'Informations de règlement'}
              </div>
              <div className="text-xs text-slate-600 space-y-1.5">
                <div className="flex justify-between">
                  <span>{isAr ? 'طريقة الأداء:' : 'Mode de paiement:'}</span>
                  <span className="font-semibold text-slate-900">{invoice.paymentMethod || 'Espèces'}</span>
                </div>
                <div className="flex justify-between">
                  <span>{isAr ? 'حالة السداد:' : 'Statut:'}</span>
                  <span className="font-semibold">{statusBadge()}</span>
                </div>
                {invoice.notes && (
                  <div className="mt-2 text-xs text-slate-500 border-t border-slate-200 pt-1.5">
                    <b>Note:</b> {invoice.notes}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                <tr>
                  <th className="px-3.5 py-2.5 text-left">{isAr ? 'البيان' : 'Description'}</th>
                  <th className="px-3 py-2.5 text-center">{isAr ? 'الكمية' : 'Qté'}</th>
                  <th className="px-3 py-2.5 text-right">{isAr ? 'السعر HT' : 'Prix HT'}</th>
                  {isManual && <th className="px-3 py-2.5 text-center">TVA %</th>}
                  {isManual && <th className="px-3 py-2.5 text-center">{isAr ? 'خصم %' : 'Remise %'}</th>}
                  <th className="px-3.5 py-2.5 text-right">{isAr ? 'المجموع' : 'Total'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isManual && Array.isArray(invoice.items) && invoice.items.length > 0 ? (
                  invoice.items.map((it, idx) => {
                    const qty = Number(it.quantity) || 1;
                    const price = Number(it.unitPrice) || 0;
                    const disc = Number(it.discountPercent) || 0;
                    const tva = Number(it.tvaPercent) || 0;
                    const netHT = (qty * price) * (1 - disc / 100);
                    const totalLine = netHT * (1 + tva / 100);

                    return (
                      <tr key={it.id || idx} className="hover:bg-slate-50/50">
                        <td className="px-3.5 py-2.5 font-medium text-slate-900">{it.description || '—'}</td>
                        <td className="px-3 py-2.5 text-center">{qty}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{price.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-center">{tva}%</td>
                        <td className="px-3 py-2.5 text-center">{disc ? `${disc}%` : '—'}</td>
                        <td className="px-3.5 py-2.5 text-right font-mono font-bold">{totalLine.toFixed(2)} {cur}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="px-3.5 py-2.5 font-medium text-slate-900">
                      {invoice.carName || 'Location de véhicule'} ({formatDateFR(invoice.startDate)} → {invoice.isOpenContract ? '⏳' : formatDateFR(invoice.endDate)})
                    </td>
                    <td className="px-3 py-2.5 text-center">{invoice.days || 1}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{Number(invoice.dailyRate || invoice.totalAmount).toFixed(2)}</td>
                    <td className="px-3.5 py-2.5 text-right font-mono font-bold">{Number(invoice.totalAmount).toFixed(2)} {cur}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Financial Breakdown */}
          <div className="flex justify-end">
            <div className="w-72 space-y-2 text-xs">
              <div className="flex justify-between text-slate-600">
                <span>{isAr ? 'المجموع دون ضريبة (HT)' : 'Sous-total HT'}:</span>
                <span className="font-mono font-semibold">{subtotalHT.toFixed(2)} {cur}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>{isAr ? 'الخصم' : 'Remise'}:</span>
                  <span className="font-mono font-semibold">-{discountAmount.toFixed(2)} {cur}</span>
                </div>
              )}
              {tvaAmount > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>{isAr ? 'مجموع الضريبة (TVA)' : 'Total TVA'}:</span>
                  <span className="font-mono font-semibold">+{tvaAmount.toFixed(2)} {cur}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold text-slate-900 border-t border-slate-300 pt-2">
                <span>{isAr ? 'المجموع الصافي (TTC)' : 'Total TTC'}:</span>
                <span className="font-mono text-base text-blue-700">{totalTTC.toFixed(2)} {cur}</span>
              </div>
              <div className="flex justify-between text-emerald-600 font-semibold">
                <span>{isAr ? 'المبلغ المؤدى' : 'Montant payé'}:</span>
                <span className="font-mono">{paidAmount.toFixed(2)} {cur}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-amber-600 border-t border-slate-200 pt-1.5">
                <span>{isAr ? 'المتبقي للأداء' : 'Reste à payer'}:</span>
                <span className="font-mono">{remainingAmount.toFixed(2)} {cur}</span>
              </div>
            </div>
          </div>

        </div>

      </div>
    </Modal>
  );
}
